/**
 * chartUtils.ts — Shared chart utilities (single source of truth).
 * v3.56 - 2026-02-25 - Add columnRoles to VisualizationMetadata for explicit axis mapping
 * v3.55 - 2026-02-24 - Story 12A.1: Extract chart type interfaces from api.ts (ADR-002)
 * v3.53 - 2026-02-24 - Story 13: Extract shared chart utilities from 5 files (ADR-002)
 *
 * Consolidates duplicated logic that accumulated across Stories 5–9H:
 *   ABBREVIATION_MAP (was in 4 files with divergent entries)
 *   formatTitle / formatColumnLabel (was in 3–4 files)
 *   BAR_TYPES / PIE_TYPES / LINE_TYPES (was only in ChartDialog)
 *   hasMixedPolarity (was in ChartDialog + chartMapper)
 *   sortReportData (was in ChartDialog, inline in DashboardCharts)
 *   calculateChartHeight (was in PlotlyChart + ChartDialog with divergent formulas)
 *
 * Type interfaces (ColumnDefinition, VisualizationMetadata, ChartReportData)
 * live here so chartMapper.ts + colorPalettes.ts can be imported by
 * chart-renderer without pulling in the full api.ts service layer.
 */

// ─── Chart Data Type Interfaces (v3.55 - moved from api.ts) ─────────────────

// v3.55 - Story 12A.1: Moved from api.ts to make chart files self-contained - 2026-02-24
export interface ColumnDefinition {
  name: string;
  type: string;  // "number" | "string"
}

// v3.55 - Story 12A.1: Moved from api.ts to make chart files self-contained - 2026-02-24
export interface VisualizationMetadata {
  chartType: string;
  chartRationale?: string;
  colorScheme?: {
    paletteType: string;
    palette: string[];
    semantics?: string;
    darkModeOverride?: string[];
  };
  labels?: {
    title: string;
    subtitle?: string;
    xAxis?: string;
    yAxis?: string;
    columnMap?: Record<string, string>;
  };
  displayRules?: {
    sortOrder?: string;
    showDataLabels?: boolean;
    showLegend?: boolean;
    maxCategories?: number;
    displayPriority?: number;
    annotations?: any[];
  };
  accessibility?: {
    altText: string;
    dataTableAvailable: boolean;
  };
  isEmpty?: boolean;
  nullsExcluded?: { count: number; column: string };
  // v1.64 - Explicit column-to-chart-role mapping from pipeline (eliminates guessing in chartMapper)
  columnRoles?: {
    category: string;   // column name for category/label axis
    value: string;      // column name for measure/value axis
    series?: string;    // column name for multi-series grouping (optional)
  };
}

// v3.55 - Story 12A.1: Moved from api.ts to make chart files self-contained - 2026-02-24
export interface ChartReportData {
  id: string;
  title: string;
  description: string;
  chartType: string;
  goldOperation: string;
  goldTable: string;
  origin: string;
  xColumn?: string;
  yColumn?: string;
  groupColumn?: string;
  category?: string;
  relatedKpiCardId?: string;
  relevanceScore?: number;
  columns: ColumnDefinition[];
  data: Record<string, any>[];
  total: number;
  visualization?: VisualizationMetadata | null;
}

// ─── Abbreviation Map (union of all 4 previous instances) ───────────────────

export const ABBREVIATION_MAP: Record<string, string> = {
  ar:    'Accounts Receivable',
  ap:    'Accounts Payable',
  gl:    'General Ledger',
  hr:    'Human Resources',
  po:    'Purchase Order',
  so:    'Sales Order',
  inv:   'Inventory',
  wip:   'Work in Progress',
  cogs:  'Cost of Goods Sold',
  ebitda: 'EBITDA',
  roi:   'Return on Investment',
  kpi:   'Key Performance Indicator',
};

// ─── Chart Type Classification ──────────────────────────────────────────────

export const BAR_TYPES  = ['horizontal_bar', 'grouped_bar', 'stacked_bar'] as const;
export const PIE_TYPES  = ['pie', 'donut'] as const;
export const LINE_TYPES = ['line', 'area'] as const;

export const isBarType  = (t: string): boolean => (BAR_TYPES as readonly string[]).includes(t);
export const isPieType  = (t: string): boolean => (PIE_TYPES as readonly string[]).includes(t);
export const isLineType = (t: string): boolean => (LINE_TYPES as readonly string[]).includes(t);

// ─── Title Formatting (replaces 3 copies of formatTitle + formatGoldTableLabel) ─

export function formatTitle(raw: string): string {
  return raw
    .replace(/^(gold_|rpt_|report_)/, '')
    .split('_')
    .map((w) => ABBREVIATION_MAP[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ─── Column Label Formatting (replaces 2 copies) ───────────────────────────

export function formatColumnLabel(colName: string): string {
  const words = colName.split('_');
  return words
    .map((w) => ABBREVIATION_MAP[w.toLowerCase()] || w)
    .join(' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Mixed Polarity Detection (replaces 2 copies) ──────────────────────────

export function hasMixedPolarity(report: ChartReportData): boolean {
  const col = report.yColumn;
  if (!col) return false;
  const nums = report.data.map((r) => r[col]).filter((v): v is number => typeof v === 'number' && v !== 0);
  return nums.some((v) => v > 0) && nums.some((v) => v < 0);
}

// ─── Sort Report Data (replaces 3 independent sort implementations) ────────

export type SortMode = 'abs' | 'positive' | 'negative';

/**
 * Sort report data by yColumn.
 * Plotly horizontal bars render y-axis bottom→top (index 0 = bottom bar,
 * last index = top bar). When `forPlotly = true`, the sort is ascending
 * so the desired "first" value ends up at the visual top of the chart.
 */
export function sortReportData(
  report: ChartReportData,
  mode: SortMode = 'abs',
  forPlotly: boolean = true,
): ChartReportData {
  const col = report.yColumn;
  if (!col) return report;

  const sorted = [...report.data].sort((a, b) => {
    const va = Number(a[col]) || 0;
    const vb = Number(b[col]) || 0;
    if (forPlotly) {
      // Ascending for Plotly bottom→top: largest value ends at top of chart
      if (mode === 'abs') return Math.abs(va) - Math.abs(vb);
      if (mode === 'positive') return va - vb;
      return vb - va;
    }
    // Descending for non-Plotly contexts (e.g., picking top N before slicing)
    if (mode === 'abs') return Math.abs(vb) - Math.abs(va);
    if (mode === 'positive') return vb - va;
    return va - vb;
  });

  return { ...report, data: sorted };
}

// ─── Chart Height Calculation (replaces 2 divergent formulas) ───────────────

/**
 * Calculate dynamic chart height based on context and chart type.
 *   popup:   ChartDialog — larger minimums (400/450)
 *   inline:  PlotlyChart — medium minimums (300/350)
 *   preview: DashboardCharts — fixed (not dynamic)
 */
export function calculateChartHeight(
  context: 'popup' | 'inline' | 'preview',
  chartType: string,
  rowCount: number,
): number {
  if (context === 'preview') return 210;

  if (isBarType(chartType) || chartType === 'waterfall') {
    const barCount = Math.min(rowCount, 20);
    if (context === 'popup') return Math.max(400, barCount * 28);
    return Math.max(300, barCount * 28 + 80); // inline has margin overhead
  }

  return context === 'popup' ? 450 : 350;
}
